import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Menu, Sun, Moon, Bell, LogOut, User, Settings, CheckCheck } from 'lucide-react';
import clsx from 'clsx';
import { toggleTheme } from '@/redux/slices/themeSlice';
import { logout } from '@/redux/slices/authSlice';
import { useAuth } from '@/hooks/useAuth';
import { notificationService } from '@/services';
import { FCM_TOKEN_KEY } from '@/hooks/usePushNotifications';
import { fmtDateTime } from '@/utils/format';

export default function Navbar({ onMenu }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const mode = useSelector((s) => s.theme.mode);
  const [showNotif, setShowNotif] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const { data } = useQuery({
    queryKey: ['notifications', 'header'],
    queryFn: () => notificationService.list({ limit: 10 }),
    refetchInterval: 60_000,
  });
  const items = data?.items || [];
  const unread = data?.unreadCount || 0;

  const handleLogout = async () => {
    // Stop pushing to this device once the user signs out.
    const fcmToken = localStorage.getItem(FCM_TOKEN_KEY);
    if (fcmToken) {
      try {
        await notificationService.unregisterToken(fcmToken);
      } catch { /* ignore */ }
      localStorage.removeItem(FCM_TOKEN_KEY);
    }
    dispatch(logout());
    navigate('/login');
  };

  const onClickNotification = async (n) => {
    setShowNotif(false);
    // Optimistically mark read
    if (!n.read) {
      try {
        await notificationService.markRead(n._id);
        qc.invalidateQueries({ queryKey: ['notifications'] });
      } catch (e) { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try {
      await notificationService.markAllRead();
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) { /* ignore */ }
  };

  return (
    <header className="sticky top-0 z-20 bg-white/80 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200 dark:border-slate-800">
      <div className="h-16 px-4 md:px-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button className="lg:hidden btn-ghost p-2" onClick={onMenu}>
            <Menu size={20} />
          </button>
          <div className="min-w-0">
            <div className="text-sm text-slate-500">Welcome back,</div>
            <div className="font-semibold truncate">{user?.name}</div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => dispatch(toggleTheme())}
            className="btn-ghost p-2"
            title="Toggle theme"
          >
            {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <div className="relative">
            <button onClick={() => setShowNotif((v) => !v)} className="btn-ghost p-2 relative">
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
            {showNotif && (
              <div
                className="absolute right-0 mt-2 w-80 card overflow-hidden animate-fade-in z-30"
                onMouseLeave={() => setShowNotif(false)}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                  <div className="font-semibold text-sm">Notifications</div>
                  <button
                    className="text-xs text-brand-600 hover:underline flex items-center gap-1"
                    onClick={markAllRead}
                  >
                    <CheckCheck size={12} /> Mark all read
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {items.length === 0 && (
                    <div className="p-6 text-sm text-slate-500 text-center">You're all caught up.</div>
                  )}
                  {items.map((n) => (
                    <button
                      key={n._id}
                      onClick={() => onClickNotification(n)}
                      className={clsx(
                        'w-full text-left px-4 py-3 border-b last:border-0 border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 flex gap-3 relative',
                        !n.read && 'bg-brand-50/50 dark:bg-brand-500/5'
                      )}
                    >
                      {!n.read && <span className="absolute left-1.5 top-4 w-1.5 h-1.5 rounded-full bg-brand-500" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</div>
                        <div className="text-[11px] text-slate-400 mt-1">{fmtDateTime(n.createdAt)}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                  <button
                    onClick={() => { setShowNotif(false); navigate('/notifications'); }}
                    className="w-full text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200 hover:bg-brand-50 dark:hover:bg-brand-500/10 py-1.5 rounded-md transition"
                  >
                    View all notifications →
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button onClick={() => setShowMenu((v) => !v)} className="flex items-center gap-2 btn-ghost px-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 grid place-items-center text-white text-sm font-semibold">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
            </button>
            {showMenu && (
              <div
                className="absolute right-0 mt-2 w-48 card overflow-hidden animate-fade-in z-30"
                onMouseLeave={() => setShowMenu(false)}
              >
                <Link to="/profile" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
                  <User size={16} /> Profile
                </Link>
                <Link to="/profile" className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800">
                  <Settings size={16} /> Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                >
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
